import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Toaster, toast } from 'react-hot-toast'
import AuthHeader from '../../components/company/registration/AuthHeader'
import RegistrationSidebar from '../../components/company/registration/RegistrationSidebar'
import StepIndicator from '../../components/company/registration/StepIndicator'
import SubscriptionPlan from '../../components/common/SubscriptionPlan'
import { companyService } from '../../services/companyService'
import { authService } from '../../services/authService'
import { companyRegistrationSteps } from '../../utils/companyRegistrationSteps'

const SubscriptionPlanPage = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [isLoading, setIsLoading] = useState(false)
    const [formData, setFormData] = useState({})
    const [userId, setUserId] = useState(null)
    const [companyId, setCompanyId] = useState(null)

    // Refs to prevent infinite loops (as requested in prompt)
    const isCheckingRef = useRef(false)
    const hasNavigatedRef = useRef(false)

    // Current Step: 3
    const currentStepConfig = companyRegistrationSteps.find(s => s.id === 3)

    useEffect(() => {
        const checkStatus = async () => {
            if (isCheckingRef.current || hasNavigatedRef.current) return
            isCheckingRef.current = true

            // Ensure we are on the right page before running check logic heavily
            if (location.pathname !== '/company/registration/subscription') return

            try {
                const currentUser = await authService.getCurrentUser()
                if (!currentUser) {
                    navigate('/auth/login')
                    hasNavigatedRef.current = true
                    return
                }
                setUserId(currentUser.id || currentUser.user_id)
                setCompanyId(sessionStorage.getItem('companyId'))

                // Check registration status
                const status = currentUser.hasCompletedRegistration

                if (status === true || status === 'true') {
                    navigate('/company/registration/documents')
                    hasNavigatedRef.current = true
                    return
                }

                // If earlier steps not done?
                if (!status || status === 'false') {
                    navigate('/company/registration/legal-details')
                    hasNavigatedRef.current = true
                    return
                }
                if (status === 'company-register') {
                    navigate('/company/registration/profile')
                    hasNavigatedRef.current = true
                    return
                }

                // If company-profile -> Stay here.

                // Load existing subscription data
                try {
                    const subData = await companyService.getSubscription(currentUser.id || currentUser.user_id)
                    if (subData) {
                        // If they already have a plan selected but status is not updated?
                        if (subData.plan_name || subData.plan) {
                            setFormData(prev => ({ ...prev, plan_name: subData.plan_name || subData.plan }))
                        }
                    }
                } catch (e) {
                    // Ignore
                }

            } catch (error) {
                console.error("Status check failed", error)
            } finally {
                isCheckingRef.current = false
            }
        }
        checkStatus()
    }, [navigate, location.pathname])

    const handleNext = async (planData) => {
        try {
            setIsLoading(true)

            // Prepare payment data
            // planData contains plan_name, billing_cycle, etc.
            const paymentPayload = {
                user_id: userId,
                company_id: companyId,
                ...planData,
                payment_method: 'Card' // Default or from UI if updated
            }

            // Prepare subscription data matching API schema
            const subscriptionPayload = {
                company_id: Number(companyId) || 0, // Ensure it's a number/valid if required
                user_id: Number(userId) || 0,       // Ensure it's a number/valid if required
                plan_name: planData.plan_name || planData.plan || 'Free',
                billing_cycle: planData.billing_cycle || 'Monthly',
                promo_code: planData.promo_code || "",
                payment_method: planData.payment_method || "Card",
                cardholder_name: planData.cardholder_name || "Test User", // Mock data if actual payment not implemented
                card_number: planData.card_number || "424242424242",      // Mock data if actual payment not implemented
                upi_id: planData.upi_id || "",
                bank_name: planData.bank_name || ""
            }

            // Calls POST /company-subscription
            await companyService.saveSubscription(subscriptionPayload)

            // Update user status
            const user = await authService.getCurrentUser()
            if (user) {
                // Determine new status. Prompt says "update hasCompletedRegistration to true"
                const updatedUser = { ...user, hasCompletedRegistration: true }
                sessionStorage.setItem('userData', JSON.stringify(updatedUser))
                sessionStorage.setItem('hasCompletedRegistration', JSON.stringify(true))
            }

            localStorage.setItem('company_registration_step3', JSON.stringify(planData))

            toast.success('Subscription active! Proceeding to document upload.')
            navigate('/company/registration/documents')

        } catch (error) {
            console.error(error)
            toast.error(error.message || 'Payment processing failed')
        } finally {
            setIsLoading(false)
        }
    }

    const handlePrevious = () => {
        navigate('/company/registration/profile')
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900">
            <AuthHeader />
            <div className="flex flex-col items-center min-h-[calc(100vh-64px)] p-4 py-4 gap-4">
                {/* Step Indicator */}
                <div className="w-full max-w-6xl">
                    <StepIndicator currentStep={3} totalSteps={companyRegistrationSteps.length} />
                </div>

                <div className="flex items-stretch justify-center w-full gap-4">
                    {/* Sidebar */}
                    <div className="hidden lg:block">
                        <RegistrationSidebar currentStep={3} steps={companyRegistrationSteps} />
                    </div>

                    {/* Main Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card w-full max-w-6xl p-5 md:p-6 shadow-glow flex-1 flex flex-col bg-white/80 dark:bg-slate-800/90 backdrop-blur-xl border border-secondary-200 dark:border-secondary-700 rounded-2xl"
                    >
                        {/* Header */}
                        <div className="text-center mb-6 pb-6 border-b border-secondary-200 dark:border-secondary-700">
                            <h1 className="text-3xl font-display font-bold text-secondary-900 dark:text-secondary-100 mb-2 tracking-tight">
                                {currentStepConfig?.title || 'Choose Your Plan'}
                            </h1>
                            <p className="text-sm text-secondary-600 dark:text-secondary-400 max-w-md mx-auto leading-relaxed">
                                {currentStepConfig?.description || 'Select the perfect plan for your company'}
                            </p>
                        </div>

                        <div className="flex-1">
                            <SubscriptionPlan
                                formData={formData}
                                onNext={handleNext}
                                isLoading={isLoading}
                            />

                            <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
                                <button
                                    onClick={handlePrevious}
                                    disabled={isLoading}
                                    className="px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                                >
                                    Back
                                </button>

                                <button
                                    onClick={() => document.querySelector('form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))}
                                    disabled={isLoading}
                                    className="px-8 py-2.5 text-sm font-semibold bg-blue-600 text-white rounded-xl shadow-lg hover:shadow-xl hover:bg-blue-700 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? (
                                        <span className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Processing...
                                        </span>
                                    ) : 'Proceed to Payment'}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    )
}

export default SubscriptionPlanPage
